export const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || 
  process.env.REACT_APP_API_BASE_URL || 
  (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://chat-backend-59v1.onrender.com');

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Token refresh function
async function refreshToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      return data.access_token;
    }
  } catch (error) {
    console.warn('Token refresh failed:', error);
  }

  // Refresh failed, clear tokens
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('cached_user');
  localStorage.removeItem('user_cache_timestamp');
  return null;
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  let token = localStorage.getItem('access_token');
  
  const makeRequest = async (authToken: string | null) => {
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.detail || errorData.message || `HTTP error! status: ${response.status}`;
      console.error('API Error Details:', {
        status: response.status,
        url,
        errorData,
        errorMessage
      });
      throw new ApiError(
        response.status,
        errorMessage
      );
    }

    return await response.json();
  };

  try {
    return await makeRequest(token);
  } catch (error) {
    // If we get a 401 and it's not a login/register endpoint, try to refresh token
    if (error instanceof ApiError && 
        error.status === 401 && 
        !endpoint.includes('/auth/login') && 
        !endpoint.includes('/auth/register') &&
        !endpoint.includes('/auth/refresh')) {
      
      const newToken = await refreshToken();
      if (newToken) {
        // Retry with new token
        return await makeRequest(newToken);
      }
    }
    
    throw error;
  }
}

export const authApi = {
  register: async (credentials: {
    email: string;
    password: string;
    full_name: string;
  }) => {
    return apiRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  login: async (credentials: { email: string; password: string }) => {
    return apiRequest<{ 
      access_token: string; 
      refresh_token: string;
      user: any;
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  getProfile: async () => {
    return apiRequest('/api/auth/me');
  },

  updateProfile: async (updates: {
    full_name?: string;
    email?: string;
  }) => {
    return apiRequest<any>('/api/auth/update-profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  changePassword: async (data: {
    current_password: string;
    new_password: string;
  }) => {
    return apiRequest('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  resetPassword: async (data: {
    token: string;
    new_password: string;
  }) => {
    return apiRequest('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  refreshToken: async (refreshToken: string) => {
    return apiRequest<{ 
      access_token: string; 
      refresh_token?: string;
    }>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  },
};

export const adminApi = {
  checkAdminStatus: async () => {
    return apiRequest<{
      is_admin: boolean;
      admin_level?: number;
      email: string;
    }>('/api/admin/is_admin');
  },

  updateCompanion: async <T = any>(companionId: string, updates: any): Promise<T> => {
    return apiRequest<T>(`/api/admin/companions/${companionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  deleteCompanionAsset: async (companionId: string, assetId: string): Promise<void> => {
    return apiRequest<void>(`/api/admin/companions/${companionId}/assets/${assetId}`, {
      method: 'DELETE',
    });
  },
};